import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import MediumshipLogo from './MediumshipLogo';
import './Navigation.css';

function primaryTabsFor(role) {
  if (role === 'practitioner' || role === 'admin') {
    return [
      { to: '/practitioner/dashboard', label: 'Home', icon: '🏠' },
      { to: '/practitioner/services', label: 'Services', icon: '✨' },
      { to: '/practitioner/crm', label: 'Clients', icon: '👥' }
    ];
  }
  return [
    { to: '/', label: 'Home', icon: '🏠' },
    { to: '/services', label: 'Services', icon: '✨' },
    { to: '/bookings', label: 'Bookings', icon: '📅' },
    { to: '/invoices', label: 'Invoices', icon: '💳' }
  ];
}

// Secondary links, bucketed into named groups so the desktop nav can collapse
// each into a single dropdown instead of laying every link out in a row -
// that flat-row approach is what caused the topbar to overflow horizontally
// once the practitioner/admin link count grew. A group with no `label` is
// short enough to render as plain inline links instead of a dropdown.
function navGroupsFor(role) {
  const groups = [];
  if (role === 'practitioner' || role === 'admin') {
    groups.push({
      key: 'tools',
      label: 'Tools',
      links: [
        { to: '/', label: 'Client View' },
        { to: '/practitioner/profile', label: 'Public Profile' },
        { to: '/practitioner/invoices', label: 'Invoices' },
        { to: '/practitioner/reports', label: 'Reports' }
      ]
    });
    groups.push({
      key: 'marketing',
      label: 'Marketing',
      links: [
        { to: '/practitioner/email-templates', label: 'Email Templates' },
        { to: '/practitioner/campaigns', label: 'Campaigns' },
        { to: '/practitioner/short-urls', label: 'Short URLs' }
      ]
    });
  }
  if (role === 'admin') {
    // Management functions (users/services/coupons/bookings) live as
    // breakout cards inside the Admin Dashboard page itself, so the nav
    // only needs a single entry point rather than one link per function.
    groups.push({ key: 'admin', label: null, mobileLabel: 'Admin', links: [{ to: '/admin', label: 'Admin Dashboard' }] });
  }
  if (role === 'client') {
    groups.push({ key: 'client', label: null, links: [{ to: '/booking', label: 'Book a Session' }] });
  }
  groups.push({ key: 'account', label: null, mobileLabel: 'Account', links: [{ to: '/profile', label: 'My Profile' }] });
  return groups;
}

function Navigation({ user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState(null);
  const navRef = useRef(null);

  const primaryTabs = primaryTabsFor(user?.role);
  const groups = navGroupsFor(user?.role);
  const isActive = (to) => (to === '/' ? location.pathname === '/' : location.pathname.startsWith(to));
  const isGroupActive = (group) => group.links.some((link) => isActive(link.to));

  // Route changes close whatever menu/dropdown was open, so navigating never
  // leaves a stale panel hanging over the new page.
  useEffect(() => {
    setMenuOpen(false);
    setOpenGroup(null);
  }, [location.pathname]);

  // A dropdown closes on an outside click or Escape - standard menu behavior,
  // and without it a click meant for the page underneath would be swallowed.
  useEffect(() => {
    if (!openGroup) return undefined;
    const handleClick = (e) => {
      if (!navRef.current?.contains(e.target)) setOpenGroup(null);
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') setOpenGroup(null);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [openGroup]);

  const handleLogout = () => {
    setMenuOpen(false);
    onLogout();
    navigate('/login');
  };

  return (
    <>
      <header className="topbar">
        <Link to="/" className="topbar-logo">
          <MediumshipLogo variant="horizontal" size={26} />
        </Link>
        <div className="topbar-links" ref={navRef}>
          {primaryTabs.map((tab) => (
            <Link
              key={tab.to}
              to={tab.to}
              className={`topbar-link ${isActive(tab.to) ? 'active' : ''}`}
            >
              {tab.label}
            </Link>
          ))}
          {groups.map((group) => {
            if (!group.label) {
              return group.links.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`topbar-link ${isActive(link.to) ? 'active' : ''}`}
                >
                  {link.label}
                </Link>
              ));
            }
            const isOpen = openGroup === group.key;
            return (
              <div className="nav-dropdown" key={group.key}>
                <button
                  type="button"
                  className={`topbar-link nav-dropdown-trigger ${isGroupActive(group) ? 'active' : ''}`}
                  aria-expanded={isOpen}
                  onClick={() => setOpenGroup(isOpen ? null : group.key)}
                >
                  {group.label} <span className="nav-dropdown-caret">{isOpen ? '▴' : '▾'}</span>
                </button>
                {isOpen && (
                  <div className="nav-dropdown-panel">
                    {group.links.map((link) => (
                      <Link
                        key={link.to}
                        to={link.to}
                        className={`nav-dropdown-link ${isActive(link.to) ? 'active' : ''}`}
                        onClick={() => setOpenGroup(null)}
                      >
                        {link.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="topbar-user">
          {user?.role === 'admin' && <span className="badge badge-admin">Admin</span>}
          <span className="topbar-username">{user?.name}</span>
          <button onClick={handleLogout} className="btn btn-ghost btn-sm">
            Logout
          </button>
        </div>
        <button
          className="topbar-menu-btn"
          onClick={() => setMenuOpen((open) => !open)}
          aria-label="Menu"
        >
          {menuOpen ? '✕' : '☰'}
        </button>
      </header>

      {menuOpen && (
        <div className="mobile-menu" onClick={() => setMenuOpen(false)}>
          <div className="mobile-menu-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="mobile-menu-user">
              <div>
                <div style={{ fontWeight: 700 }}>{user?.name}</div>
                <div className="text-muted" style={{ fontSize: '0.85rem' }}>{user?.email}</div>
              </div>
              {user?.role === 'admin' && <span className="badge badge-admin">Admin</span>}
            </div>
            {/* The sheet fully covers the bottom tab bar while open, so the
                primary tabs (Home/Services/Clients etc.) need to be repeated
                here too - otherwise they're unreachable until the menu closes. */}
            <div className="mobile-menu-section">
              {primaryTabs.map((tab) => (
                <Link
                  key={tab.to}
                  to={tab.to}
                  className="mobile-menu-link"
                  onClick={() => setMenuOpen(false)}
                >
                  <span style={{ marginRight: 8 }}>{tab.icon}</span>
                  {tab.label}
                </Link>
              ))}
            </div>
            {groups.map((group) => (
              <div key={group.key} className="mobile-menu-section">
                {(group.label || group.mobileLabel) && (
                  <div className="mobile-menu-section-label">{group.label || group.mobileLabel}</div>
                )}
                {group.links.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className="mobile-menu-link"
                    onClick={() => setMenuOpen(false)}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            ))}
            <button onClick={handleLogout} className="btn btn-danger btn-block" style={{ marginTop: 10 }}>
              Logout
            </button>
          </div>
        </div>
      )}

      <nav className="bottom-tabs">
        {primaryTabs.map((tab) => (
          <Link
            key={tab.to}
            to={tab.to}
            className={`bottom-tab ${isActive(tab.to) ? 'active' : ''}`}
          >
            <span className="bottom-tab-icon">{tab.icon}</span>
            <span className="bottom-tab-label">{tab.label}</span>
          </Link>
        ))}
        <button className="bottom-tab" onClick={() => setMenuOpen(true)}>
          <span className="bottom-tab-icon">☰</span>
          <span className="bottom-tab-label">More</span>
        </button>
      </nav>
    </>
  );
}

export default Navigation;
